import { useCallback, useEffect, useState } from 'react';

/**
 * The non-standard event Chromium fires when the app is installable. Not in
 * the DOM lib typings, so declared locally.
 */
interface IBeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface IUseInstallPromptReturn {
  canInstall: boolean;
  promptInstall: () => Promise<void>;
}

function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches;
}

/**
 * Captures the browser's deferred `beforeinstallprompt` event so the app can
 * offer a custom "Install app" affordance. Stays hidden once the app is
 * installed or already running in standalone display mode (#153).
 */
function useInstallPrompt(): IUseInstallPromptReturn {
  const [deferredPrompt, setDeferredPrompt] = useState<IBeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (isStandalone()) return undefined;

    const onBeforeInstallPrompt = (event: Event): void => {
      event.preventDefault();
      setDeferredPrompt(event as IBeforeInstallPromptEvent);
    };
    const onAppInstalled = (): void => setDeferredPrompt(null);

    // NOTE: Chromium defers `beforeinstallprompt` until after page load, so
    // attaching the listener in this effect is reliable in practice. If a
    // future browser fires it synchronously during parse, the event would be
    // missed and the custom button simply wouldn't appear (native install
    // affordance still works).
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    return (): void => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<void> => {
    const prompt = deferredPrompt;
    if (!prompt) return;
    try {
      await prompt.prompt();
      await prompt.userChoice;
    } finally {
      // Whether accepted, dismissed, or rejected, the install opportunity is
      // spent — clear it so the button doesn't hang on a second click.
      setDeferredPrompt(null);
    }
  }, [deferredPrompt]);

  return { canInstall: deferredPrompt !== null, promptInstall };
}

export default useInstallPrompt;
